import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import useAuthStore from "../../../store/useAuthStore";

const AgentDetails = ({ onProfileChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const user = useAuthStore((state) => state.user);
    const [formData, setFormData] = useState({
        agent_id: "",
        name: "",
        email: "",
        phone_number: "",
    });

    // Fetch Profile Data on Mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                // Try from store first
                if (user) {
                    setFormData({
                        agent_id: user.agentUniqueId || user.ducktail_id || user.agent_id || "",
                        name: user.name || "",
                        email: user.email || "",
                        phone_number: user.phone || user.phone_number || "",
                    });
                }

                const response = await axiosInstancev1.get("/real-estate-agents/auth/profile");
                if (response.data && response.data.success) {
                    const agent = response.data.profile || response.data.user || response.data;
                    setFormData({
                        agent_id: agent.agentUniqueId || agent.ducktail_id || agent.agent_id || agent.agent_id_unique || "",
                        name: agent.name || "",
                        email: agent.email || "",
                        phone_number: agent.phone || agent.phone_number || "",
                    });
                }
            } catch (error) {
                console.error("Error fetching profile:", error);

                if (user) {
                    setFormData({
                        agent_id: user.agentUniqueId || user.ducktail_id || user.agent_id || "",
                        name: user.name || "",
                        email: user.email || "",
                        phone_number: user.phone || user.phone_number || "",
                    });
                } else {
                    toast.error("Failed to load profile details.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [user]);

    // Notify parent whenever formData changes
    useEffect(() => {
        if (onProfileChange) {
            onProfileChange(formData);
        }
    }, [formData, onProfileChange]);

    const handleToggleEdit = () => {
        if (isEditing) {
            setLoading(true);
            axiosInstancev1.get("/real-estate-agents/auth/profile")
                .then(res => {
                    const agent = res.data.profile || res.data.user || res.data;
                    setFormData({
                        agent_id: agent.agentUniqueId || agent.ducktail_id || agent.agent_id || agent.agent_id_unique || "",
                        name: agent.name || "",
                        email: agent.email || "",
                        phone_number: agent.phone || agent.phone_number || "",
                    });
                })
                .catch(() => {
                    // Fallback to store
                    if (user) {
                        setFormData({
                            agent_id: user.agentUniqueId || user.ducktail_id || user.agent_id || "",
                            name: user.name || "",
                            email: user.email || "",
                            phone_number: user.phone || user.phone_number || "",
                        });
                    }
                })
                .finally(() => setLoading(false));
        }
        setIsEditing(!isEditing);
    };

    const handleInputChange = (key, value) => {
        let processedValue = value;
        if (key === "phone_number") {
            processedValue = value.replace(/\D/g, "").slice(0, 10);
        }
        setFormData({ ...formData, [key]: processedValue });
    };

    const validateForm = () => {
        if (!formData.name?.trim()) {
            toast.error("Name cannot be empty.");
            return false;
        }
        if (
            formData.phone_number &&
            (formData.phone_number.length !== 10 ||
                !/^\d{10}$/.test(formData.phone_number))
        ) {
            toast.error("Phone Number must be 10 numeric digits.");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            await axiosInstancev1.put("/real-estate-agents/auth/profile", formData);
            toast.success("Profile updated successfully!");
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating profile:", error);
            const msg = error.response?.data?.message || "Failed to update profile.";
            toast.error(msg);
        }
    };

    const fields = [
        { key: "agent_id", label: "Ducktail ID", editable: false },
        { key: "name", label: "Name", editable: true, required: true },
        { key: "email", label: "Email", editable: false },
        {
            key: "phone_number",
            label: "Phone Number",
            editable: true,
            required: false,
            type: "tel",
        },
    ];

    if (loading) {
        return <div className="text-center py-4">Loading profile...</div>;
    }

    return (
        <div className="py-2 text-xs">
            <div className="flex items-center justify-between mb-3 border-b border-gray-400 pb-2">
                <h2 className="text-sm font-semibold">My Details</h2>
                <button
                    onClick={handleToggleEdit}
                    className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
                >
                    {isEditing ? "Cancel" : "Edit"}
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fields.map(
                    ({
                        key,
                        label,
                        type,
                        options,
                        editable = true,
                        required = false,
                    }) => {
                        const isPhoneField = key === "phone_number";
                        return (
                            <div key={key} className="p-1">
                                <label className="block text-xs font-medium text-gray-600">
                                    {label}{" "}
                                    {required && isEditing && (
                                        <span className="text-red-500">*</span>
                                    )}
                                </label>
                                {type === "select" ? (
                                    <select
                                        value={formData[key] || ""}
                                        onChange={(e) => handleInputChange(key, e.target.value)}
                                        disabled={!editable || !isEditing}
                                        className={`w-full p-2 mt-1 rounded-md text-xs ${!editable || !isEditing
                                            ? "border-none bg-gray-50 cursor-default"
                                            : "border border-gray-300 bg-white"
                                            }`}
                                    >
                                        {options.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt || "-- Select --"}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="relative mt-1">
                                        {isEditing && isPhoneField && (
                                            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                                                +91
                                            </span>
                                        )}
                                        <input
                                            type={type === "tel" ? "tel" : "text"}
                                            inputMode={isPhoneField ? "numeric" : "text"}
                                            value={
                                                !isEditing && isPhoneField && formData[key]
                                                    ? `+91 ${formData[key]}`
                                                    : formData[key] || ""
                                            }
                                            onChange={(e) => handleInputChange(key, e.target.value)}
                                            disabled={!editable || !isEditing}
                                            className={`w-full p-2 rounded-md text-xs ${isEditing && isPhoneField ? "pl-10" : ""
                                                } ${!editable
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : isEditing
                                                        ? "border border-gray-300 bg-white"
                                                        : "border-none bg-gray-50 cursor-default"
                                                }`}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    }
                )}
            </div>
            {isEditing && (
                <div className="flex justify-end mt-3 p-2">
                    <button
                        className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs"
                        onClick={handleSubmit}
                    >
                        Save Changes
                    </button>
                </div>
            )}
        </div>
    );
};

export default AgentDetails;
